/**
 * The colour each journey access tag wears — ONE map, read by every portal.
 *
 * A journey card says three things in tags: whether it is unlocked, how much
 * of it the citizen can use, and who funds it. The web and the phone used to
 * colour them apart — the phone painted Unlocked AND Full access the same
 * green (MOB-45: colour for state only), the web painted Full access in the
 * brand violet and each funder its own hue — so one journey looked different
 * in each app. The owner chose the web's palette (3 Oct 2026); it lives here
 * so neither app can drift from it again. The web paints a tone with
 * `TONE_CLASSES` (@tonnie/ui), the phone with `TONE_HEX`.
 *
 * Locked and basic access wear no tone: the neutral style of each portal.
 * Colour is never the only signal — every tag also has its label, and the
 * phone gives each funder an icon.
 */
import type { Tone } from './tones';

export type JourneyAccessTag =
  | 'unlocked'
  | 'fullAccess'
  | 'sponsoredCompany'
  | 'sponsoredGovernment'
  | 'family'
  | 'pro'
  | 'invited'
  | 'setupPending'
  | 'paused'
  | 'expired';

export const JOURNEY_ACCESS_TONE: Record<JourneyAccessTag, Tone> = {
  /** Everything is open — the state you want. */
  unlocked: 'green',
  /** How much of it you can use: all of it — the brand's own hue. */
  fullAccess: 'violet',
  sponsoredCompany: 'blue',
  sponsoredGovernment: 'violet',
  family: 'amber',
  pro: 'violet',
  invited: 'cyan',
  /** Waiting on the citizen (an invitation's onboarding) or on a payment. */
  setupPending: 'amber',
  paused: 'amber',
  /** The one state that stops the citizen. */
  expired: 'red',
};
